import * as React from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Container from "@mui/material/Container";
import Header from "./Header";
import LeftMenu from "./LeftMenu";
import { ProcessReducer } from "@/redux/modules/Process";
import { useDispatch, useSelector } from "react-redux";
import PopupSnackBar from "./PopupSnackBar";
import PopupBackDrop from "./PopupBackDrop";
import pm from "@/core/helpers/ProcessManager";
import PopupConfirm from "./PopupConfirm";
import QueueMenu from "./QueueMenu";
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { CurrencyModel, DepartmentModel } from '@/interface';
import { setDepartments } from '@/redux/modules/department/departmentActions';
import { StateReducer } from '@/redux/modules/reducer';
import { BACKEND_URL, isProdHost } from "@/core/config";
import { actions as helpdeskActions, filterFreeOrOwnTickets } from "@/redux/modules/Helpdesk";
import { api } from '@/api/Api';
import { useLocation } from 'react-router-dom';
import { selectActiveDepartment, selectAllInitialDepartments } from '@/redux/modules/department/departmentSelectors';
import { syncSessionDepartment } from '@/redux/modules/department/departmentActions';
import NorthIcon from '@mui/icons-material/North';
import SouthIcon from '@mui/icons-material/South';
import { actions } from '@/redux/modules/Header';
import { filterDepartmentsByRole, hasAnyRole, hasDepartmentScope } from "@/core/helpers/PageHelpers";
import { redirectToLogin } from "@/core/server/axios";
import { clearSession } from "@/core/helpers/session";
import { useCharterCapital } from "./CharterCapital/useCharterCapital";

const authorizedLayout = (Component: React.FC) => (
    <MainLayout>
        <Component />
    </MainLayout>
);

const mdTheme = createTheme({});

/**
 * Страницы, которым нужна вся область под шапкой без полей контейнера:
 * встроенный фрейм рисует свой интерфейс, и наши отступы только рамкой
 * обводили бы чужую страницу.
 */
const FULL_BLEED_ROUTES = ["/analytics"];

export const MainLayout = ({ children }: { children: React.ReactNode }) => {
    const location = useLocation();
    const dispatch = useDispatch();

    const processReducer: ProcessReducer = useSelector((state: StateReducer) => state.process);
    const userLogin = useSelector((state: StateReducer) => state.header.userContext?.user?.login || "");
    const userRoles = useSelector((state: StateReducer) => state?.header?.userContext?.user?.roles);
    const { snackBar, backdrop, confirm } = processReducer;

    const [nowIsOvertime, setNowIsOvertime] = React.useState<boolean>(false);
    const isFullBleed = FULL_BLEED_ROUTES.some((path) => location.pathname.startsWith(path));
    const cashierRequestQueue = useSelector((state: StateReducer) => state.header.cashierRequestQueue);

    const activeDepartmentRef = React.useRef<DepartmentModel | null>(null);
    const activeDepartment = useSelector(selectActiveDepartment);
    const rolesData = useSelector((state: StateReducer) => state?.header?.rolesData);
    // Уставной капитал по юрлицам: для ролей без доступа — пустой список
    const charterCapitalGroups = useCharterCapital();

    // Обработчик ReceiveRates регистрируется один раз (эффект с пустыми зависимостями),
    // поэтому напрямую замкнутый rolesData в нём "замерзает" на значении с момента монтирования.
    // Если к этому моменту роли ещё не подтянулись (или уже сменился пользователь),
    // фильтр по городу/тенанту не срабатывал и в стор уезжал полный список отделений.
    // Читаем роли только через ref — он всегда актуален.
    const rolesDataRef = React.useRef(rolesData);
    const userRolesRef = React.useRef(userRoles);

    React.useEffect(() => {
        rolesDataRef.current = rolesData;
        userRolesRef.current = userRoles;
    }, [rolesData, userRoles]);

    const [timerId, setTimerId] = React.useState<ReturnType<typeof setInterval>>();
    const rateConnectionRef = React.useRef<HubConnection | null>(null);
    const reconnectTimerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
    // Отметка, что снимок отделений уже пришёл по сокету: ответ REST после
    // этого применять нельзя, он затёр бы более свежие курсы и балансы.
    const socketDepartmentsRef = React.useRef(false);
    const fetchData = (acceptQueue?: boolean, cashierRequestQueue?: boolean, cashTransferQueue?: boolean, cashRequestQueue?: boolean) => {
        pm.fetchQueueData({ acceptQueue, cashierRequestQueue, cashTransferQueue, cashRequestQueue });
    };

    const checkAuthorization = async () => {
        if (!rolesData?.isTreasurerViewer && !rolesData?.franchiseTenantId && !rolesData?.isNationalBank && !rolesData?.isCollector) {
            await api.qrAuth.isAuthToday()
                .then(async (res) => {
                    if (res.depId === 0) {
                        clearSession();

                        dispatch(actions.logout());
                        redirectToLogin('isAuthToday вернул depId=0 (периодическая проверка сессии)');
                        return;
                    }

                    dispatch(syncSessionDepartment(res.depId) as any);
                })
                .catch((err) => {
                    console.error(err);
                    pm.openSnackBar("Ошибка при проверке сессии", { severity: "error" });
                });
        }
    };

    React.useEffect(() => {
        if (location.pathname.endsWith("/confirm-request")) {
            const locationArray = location.pathname.split("/");
            setNowIsOvertime(cashierRequestQueue?.some(item => (item.requestStatus === "4" || (item?.isIndividual === "true" ? item?.updatedRequestStatus === "4" : false)) && item.taskId === locationArray[2]) || false);
        } else {
            setNowIsOvertime(false);
        }
    }, [location.pathname, cashierRequestQueue]);

    const loadByRole = async () => {
        try {
            const roleCodes = (userRoles || [])?.map(role => role?.code);
            const tickets = await api.helpdesk.getTicketsByRole(roleCodes, 1, 100);
            dispatch(helpdeskActions.helpdeskList(filterFreeOrOwnTickets(tickets, userLogin, roleCodes)));
        } catch {
            pm.openSnackBar("Ошибка при загрузке заявок по роли", { severity: "error" });
        }
    };

    const loadByLogin = async () => {
        try {
            const tickets = await api.helpdesk.getTicketsByLogin(userLogin, 1, 100);
            dispatch(helpdeskActions.helpdeskAssigneeList(tickets));
        } catch {
            pm.openSnackBar("Ошибка при загрузке ваших заявок", { severity: "error" });
        }
    };

    React.useEffect(() => {
        loadByRole();
        loadByLogin();
        checkAuthorization();
        const id = setInterval(() => {
            checkAuthorization();
            loadByRole();
            loadByLogin();
        }, 120000);

        return () => {
            clearInterval(id);
        };
    }, []);

    const allDepartmentsList = useSelector(selectAllInitialDepartments);
    const cashAcceptStatusesRef = React.useRef<Map<number, number>>(new Map());

    const checkCashAcceptStatuses = async () => {
        await api.cash.getAccepts({
            depsId: [],
            from: null,
            to: null,
            page: 1,
            size: 10,
        })
            .then((res) => {
                (res || []).forEach((item) => {
                    const prevStatus = cashAcceptStatusesRef.current.get(item.id);

                    if (prevStatus === undefined) {
                        if (item.status === 0 || item.status === 4 || item.status === 5) {
                            cashAcceptStatusesRef.current.set(item.id, item.status);
                        }
                        return;
                    }

                    if (item.status === prevStatus) {
                        return;
                    }

                    if (item.status === 1 || item.status === 5 || item.status === 2 || item.status === 6) {
                        const department = allDepartmentsList?.find(dep => dep.id === item.depId);
                        const kassa = department?.kassa?.find(k => k.id === item.kassaId);
                        const isAccepted = item.status === 1 || item.status === 5;

                        pm.openSnackBar(
                            `Заявка на ${item.amount} ${item.currencyCode} (${department?.name || item.depId}, касса ${kassa?.name || item.kassaId}) ${isAccepted ? "принята" : "отклонена"}`,
                            { severity: isAccepted ? "success" : "error", location: { horizontal: "center", vertical: "top" } }
                        );
                        cashAcceptStatusesRef.current.delete(item.id);
                    } else if (item.status === 0 || item.status === 4) {
                        cashAcceptStatusesRef.current.set(item.id, item.status);
                    }
                });
            })
            .catch((error) => {
                console.error(error);
            });
    };

    React.useEffect(() => {
        if (!rolesData?.isAccountant) {
            return;
        }

        checkCashAcceptStatuses();
        const cashAcceptStatusId = setInterval(checkCashAcceptStatuses, 30000);

        return () => {
            clearInterval(cashAcceptStatusId);
        };
    }, [rolesData?.isAccountant]);

    React.useEffect(() => {
        if (timerId) {
            clearInterval(timerId);
        }
        const needAcceptQueue = hasAnyRole(rolesData, ["isAdmin", "isCashier", "isTreasurer", "isDirector", "mainCashierCity"]);
        const needCashierRequestQueue = hasAnyRole(rolesData, ["isAdmin", "isDirector", "isCashier", "isTreasurer", "mainCashierCity"]);
        const needCashTransferQueue = hasAnyRole(rolesData, ["isCashier", "mainCashierCity"]) || (!isProdHost && rolesData?.isAdmin);
        const needCashRequestQueue = hasAnyRole(rolesData, ["isCashier", "mainCashierCity", "isAccountant"]) || (!isProdHost && rolesData?.isAdmin);

        fetchData(needAcceptQueue, needCashierRequestQueue, needCashTransferQueue, needCashRequestQueue);
        const id = setInterval(() => {
            fetchData(needAcceptQueue, needCashierRequestQueue, needCashTransferQueue, needCashRequestQueue);
        }, 30000);
        setTimerId(id);

        const connectToWebSocket = async () => {
            const connection = new HubConnectionBuilder()
                .withUrl(`${BACKEND_URL}/ratesHub`, {
                    withCredentials: false,
                })
                .withAutomaticReconnect()
                .build();

            rateConnectionRef.current = connection;

            connection.on("ReceiveRates", (newDeps: DepartmentModel[]) => {
                const currentRolesData = rolesDataRef.current;

                // Пока роли не загружены (или пользователь вышел) — не пишем в стор вообще.
                // Иначе неотфильтрованный список попадёт в initialDepartments и останется там
                // на всю сессию, так как этот список пишется только один раз.
                if (!currentRolesData || !userRolesRef.current?.length) {
                    return;
                }

                const departmentList: DepartmentModel[] = filterDepartmentsByRole(newDeps, currentRolesData);

                const departmentObj: Record<number, DepartmentModel> = {};
                departmentList?.forEach(dep => departmentObj[dep.id] = dep);
                const newCurObj: Record<number, CurrencyModel> = {};

                if (activeDepartmentRef.current && (currentRolesData?.isCashier || currentRolesData?.mainCashierCity)) {
                    try {
                        const differentRatesTextArr: React.ReactNode[] = [];
                        const oldDep = activeDepartmentRef?.current;
                        const newDep = departmentObj[oldDep?.id];
                        newDep?.currencies?.forEach(cur => {
                            if (cur?.currencyId !== undefined) {
                                newCurObj[cur?.currencyId] = cur;
                            }
                        });

                        if (oldDep) {
                            oldDep?.currencies.map((cur) => {
                                const newCur = cur?.currencyId ? newCurObj[cur?.currencyId] : undefined;

                                const difObj: {
                                    buy: "up" | "down" | "",
                                    sell: "up" | "down" | "",
                                } = {
                                    buy: "",
                                    sell: "",
                                };
                                if ((newCur?.rates[0]?.buy || 0) < cur?.rates[0]?.buy) {
                                    difObj.buy = "down";
                                } else if ((newCur?.rates[0]?.buy || 0) > cur?.rates[0]?.buy) {
                                    difObj.buy = "up";
                                }

                                if ((newCur?.rates[0]?.sale || 0) < cur?.rates[0]?.sale) {
                                    difObj.sell = "down";
                                } else if ((newCur?.rates[0]?.sale || 0) > cur?.rates[0]?.sale) {
                                    difObj.sell = "up";
                                }

                                if (difObj?.buy || difObj?.sell) {
                                    differentRatesTextArr.push(<div style={{ display: "flex", gap: "5px" }} >{cur?.code}:{difObj.buy !== "" ? <div style={{ display: "flex", gap: "5px", alignItems: "center" }}> Покупка {difObj.buy === "up" ? <NorthIcon style={{ color: "#47c347", fontSize: "17px" }} fontSize="small" /> : <SouthIcon style={{ color: "#ff8d8d", fontSize: "17px" }} fontSize="small" />}</div> : ""}{difObj.sell !== "" ? <div style={{ display: "flex", gap: "5px", alignItems: "center" }}> Продажа {difObj.sell === "up" ? <NorthIcon style={{ color: "#47c347", fontSize: "17px" }} fontSize="small" /> : <SouthIcon style={{ color: "#ff8d8d", fontSize: "17px" }} fontSize="small" />}</div> : ""}</div>);
                                }
                            });
                        }

                        activeDepartmentRef.current = newDep || null;

                        if (differentRatesTextArr?.length) {
                            pm.openSnackBar(<>
                                Курсы изменились:
                                {differentRatesTextArr.map((item) => (
                                    <div key={`${item}`}>{item}</div>
                                ))}
                            </>, {
                                severity: "warning",
                                location: { horizontal: "center", vertical: "top" },
                                onClose: (event?: React.SyntheticEvent | Event, reason?: string) => {
                                    if (reason === "clickaway" || reason === "timeout") {
                                        return;
                                    }
                                    pm.closeSnackBar();
                                }
                            });
                        }
                    } catch (e) {
                        console.error("ReceiveRates error", e);
                    }
                }

                socketDepartmentsRef.current = true;

                if (hasDepartmentScope(currentRolesData)) {
                    dispatch(setDepartments(departmentList));
                } else {
                    dispatch(setDepartments(moveAport2AfterAport(departmentList)));
                }
            });

            // При авто-переподключении соединение восстанавливается, но актуальный
            // снимок отделений (с балансами) заново не приходит — за время обрыва
            // события ReceiveRates теряются, и баланс в Redux замирает на состоянии
            // до обрыва. Поэтому после реконнекта повторно запрашиваем снимок.
            connection.onreconnecting((error) => {
                console.warn("Reconnecting to ratesHub...", error);
            });

            connection.onreconnected(async (connectionId) => {
                console.log("Reconnected to ratesHub", connectionId);
                if (reconnectTimerRef.current) {
                    clearInterval(reconnectTimerRef.current);
                    reconnectTimerRef.current = null;
                }
                try {
                    await connection.invoke("DepListHub");
                } catch (error) {
                    console.error("Не удалось обновить данные после переподключения", error);
                }
            });

            connection.onclose(() => {
                console.warn("ratesHub connection closed");

                // Если .withAutomaticReconnect не справился, включаем ручной reconnect
                if (connection.state !== "Disconnected" || reconnectTimerRef.current) return;

                const startTime = Date.now();
                const maxReconnectDuration = 30 * 60 * 1000; // 30 минут

                reconnectTimerRef.current = setInterval(async () => {
                    const elapsed = Date.now() - startTime;
                    if (elapsed > maxReconnectDuration) {
                        clearInterval(reconnectTimerRef.current!);
                        reconnectTimerRef.current = null;
                        return;
                    }

                    if (connection.state === "Disconnected") {
                        try {
                            await connection.start();
                            await connection.invoke("DepListHub");
                            clearInterval(reconnectTimerRef.current!);
                            reconnectTimerRef.current = null;
                        } catch (err) {
                            console.warn("Ошибка ручного переподключения, пробуем снова...", err);
                        }
                    }
                }, 5000);
            });

            try {
                await connection.start();
                connection.invoke("DepListHub");
            } catch (error) {
                console.error(error);
            }
        };

        /**
         * Список отделений живёт в сторе только пока открыта вкладка - в
         * localStorage мы его не сохраняем, чтобы после перезагрузки на экране
         * не висели вчерашние курсы и балансы. Но соединение с ratesHub
         * поднимается заметно дольше первого рендера, и всё это время выпадашка
         * выбора отделения пустая, а активную кассу не с чем сопоставить.
         * Поэтому сразу тянем снимок по REST, а сокет потом перезапишет его.
         */
        const loadDepartments = async () => {
            try {
                const deps: DepartmentModel[] = await api.department.getDepartments();
                const currentRolesData = rolesDataRef.current;

                // Пока роли не загружены (или пользователь вышел) - не пишем в
                // стор: неотфильтрованный список осел бы в initialDepartments.
                if (!currentRolesData || !userRolesRef.current?.length || socketDepartmentsRef.current) {
                    return;
                }

                const departmentList = filterDepartmentsByRole(deps, currentRolesData);

                dispatch(setDepartments(
                    hasDepartmentScope(currentRolesData) ? departmentList : moveAport2AfterAport(departmentList)
                ));
            } catch (error) {
                // молча: список всё равно приедет по сокету
                console.error("Не удалось загрузить список отделений", error);
            }
        };

        loadDepartments();
        connectToWebSocket();

        return () => {
            clearInterval(id);

            if (reconnectTimerRef.current) {
                clearInterval(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }

            if (rateConnectionRef.current) {
                rateConnectionRef.current?.stop();
            }
        };
    }, []);

    React.useEffect(() => {
        activeDepartmentRef.current = activeDepartment || null;
    }, [activeDepartment]);

    React.useEffect(() => {
        const handleServiceWorkerMessage = (event: MessageEvent) => {
            if (event.data && event.data.type === 'PLAY_NOTIFICATION_SOUND') {
                try {
                    const audio = new Audio('/notification-sound.mp3');
                    audio.volume = 0.7;

                    audio.play()
                        .then(() => console.log('Sound played successfully'))
                        .catch((error) => {
                            console.warn('Failed to play sound:', error);
                            // Если не удалось воспроизвести, можно использовать системный звук
                        });
                } catch (error) {
                    console.error('Error creating audio:', error);
                }
            }
        };

        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
        }

        return () => {
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
            }
        };
    }, []);

    function moveAport2AfterAport(arr: DepartmentModel[]) {
        const aportIndex = arr.findIndex(i => i.code === "АПОРТ");
        const aport2Index = arr.findIndex(i => i.code === "АПОРТ2");

        // если кого-то нет или уже стоит правильно — ничего не трогаем
        if (
            aportIndex === -1 ||
            aport2Index === -1 ||
            aport2Index === aportIndex + 1
        ) {
            return arr;
        }

        const result = [...arr];
        const [aport2] = result.splice(aport2Index, 1);
        result.splice(aportIndex + 1, 0, aport2);

        return result;
    }

    return (
        <ThemeProvider theme={mdTheme}>
            <Box sx={{ display: "flex" }}>
                <PopupSnackBar onClose={() => pm.closeSnackBar()} {...snackBar} />
                <PopupBackDrop {...backdrop} />
                <PopupConfirm
                    handleDisagreeOrClose={() => pm.closeSnackBar()}
                    {...confirm}
                />
                <CssBaseline />
                {!nowIsOvertime && (
                    <>
                        <Header charterCapitalGroups={charterCapitalGroups} />
                        <LeftMenu />
                    </>
                )}

                <QueueMenu />
                <Box
                    component="main"
                    sx={{
                        backgroundColor: (theme) =>
                            theme.palette.mode === "light"
                                ? theme.palette.grey[100]
                                : theme.palette.grey[900],
                        flexGrow: 1,
                        height: "100vh",
                        overflow: isFullBleed ? "hidden" : "auto",
                        ...(isFullBleed && { display: "flex", flexDirection: "column" })
                    }}>
                    <Toolbar />

                    <Container
                        maxWidth={false}
                        disableGutters={isFullBleed}
                        sx={isFullBleed
                            ? { p: 0, flex: 1, minHeight: 0, display: "flex" }
                            : { mt: 4, mb: 4 }}
                    >
                        {children}
                    </Container>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default authorizedLayout;
